import { IconDivider } from '@hivebrite/design-system/Icon';
import { useTheme } from '@hivebrite/design-system/Theme';

import Drop from 'hb-builder/components/common/Drop';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper, WrapperBuilder } from 'hb-builder/components/common/Wrapper';
import {
  useComponentContext,
  useBuilderConfigContext,
  useRendererContext,
} from 'hb-builder/hooks';
import { getJustifyContent } from 'hb-builder/utils/attributes';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { DividerItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/dividerItemAttributes';

import DividerUI from './components/DividerUI';
import DividerSettings from './DividerSettings';

const componentType = 'single';

const Divider = () => {
  // This is intentional
};

const RenderBuilder = () => {
  const { item } = useComponentContext();

  const {
    hivebrite: { networkColors },
  } = useBuilderConfigContext();

  const attributes = item?.attributes as DividerItemAttributes;
  const defaultBgColor = networkColors?.button_primary;

  return (
    <Drop accept={['single']}>
      {() => (
        /* @ts-expect-error untyped */
        <WrapperBuilder
          attributes={attributes}
          style={{
            justifyContent: getJustifyContent(attributes?.horizontalAlign),
          }}
        >
          {({ isDragging }) =>
            isDragging ? null : (
              <DividerUI
                attributes={attributes}
                defaultBgColor={defaultBgColor}
              />
            )
          }
        </WrapperBuilder>
      )}
    </Drop>
  );
};

const Render = ({ item }: RenderProps<DocumentItem<DividerItemAttributes>>) => {
  const { isMobilePreview } = useRendererContext();
  const theme = useTheme();

  if (!item) {
    return null;
  }
  const { attributes } = item;

  return (
    /* @ts-expect-error untyped */
    <Wrapper
      attributes={attributes}
      componentType={componentType}
      isMobilePreview={isMobilePreview}
      style={{
        justifyContent: getJustifyContent(attributes?.horizontalAlign),
      }}
    >
      <DividerUI
        attributes={attributes}
        defaultBgColor={theme.colors.primary}
      />
    </Wrapper>
  );
};

Divider.componentName = 'Divider';
Divider.componentType = componentType;

Divider.componentLabel = __('Divider');
Divider.componentCategory = 'single';
Divider.componentIcon = () => IconDivider;

Divider.canBeDoubleClicked = true;
Divider.canBeDragged = true;
Divider.canBeListed = true;
Divider.canBeSelected = true;

const defaultConfig = () => ({
  containerPadding: '20px 0px 20px 0px',
  dividerHeight: 4,
  dividerStyle: 'solid',
  dividerWidth: 100,
});

Divider.item = ({ features }) => {
  const dividerConfig = {
    ...defaultConfig(),
    ...(features?.divider || {}),
  };

  return {
    attributes: {
      containerPadding: dividerConfig.containerPadding,
      dividerColor: dividerConfig.dividerColor,
      dividerHeight: dividerConfig.dividerHeight,
      dividerStyle: dividerConfig.dividerStyle,
      dividerWidth: dividerConfig.dividerWidth,
    },
    category: Divider.componentCategory,
    name: Divider.componentName,
    type: Divider.componentName,
  };
};

Divider.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

Divider.Settings = DividerSettings;
Divider.SettingsTabs = [];

Divider.RenderBuilder = RenderBuilder;
Divider.Render = Render;

export default Divider;
